import { Card, Empty } from "antd";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { INK, CATEGORICAL, compactAmount, fullAmount } from "../crmDashboardUtils";

const BAR = CATEGORICAL[0];

const RevenueTip = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="crm-tip">
      <div className="crm-tip-title">{p.fullLabel}</div>
      <div className="crm-tip-row">
        <i style={{ background: BAR }} />
        <span>Won revenue</span>
        <b>{fullAmount(p.revenue)} {currency}</b>
      </div>
      <div className="crm-tip-muted">{p.won} deal{p.won === 1 ? "" : "s"} won</div>
    </div>
  );
};

const RevenueTrend = ({ data = [], currency, country }) => {
  const total = data.reduce((s, d) => s + d.revenue, 0);
  const rows = data.map((d, i) => ({
    ...d,
    fullLabel: `${d.label} ${String(d.month).slice(0, 4)}`,
    isLatest: i === data.length - 1,
  }));

  return (
    <Card className="crm-card" variant="borderless">
      <div className="crm-card-head">
        <div>
          <div className="crm-card-title">Won revenue by month</div>
          <div className="crm-card-sub">
            <b className="crm-card-figure">{compactAmount(total, country)}</b> {currency} in the last 6 months
          </div>
        </div>
      </div>

      {total === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No won deals in the last 6 months" style={{ padding: "32px 0" }} />
      ) : (
        <div className="crm-chart" role="img" aria-label={`Won revenue per month, ${compactAmount(total, country)} ${currency} in total`}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={rows} margin={{ top: 22, right: 8, left: -8, bottom: 0 }} barCategoryGap="32%">
              <CartesianGrid stroke={INK.grid} strokeOpacity={0.7} vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: INK.axis }}
                tick={{ fill: INK.muted, fontSize: 11 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: INK.muted, fontSize: 11 }}
                width={46}
                tickFormatter={(v) => compactAmount(v, country)}
              />
              <Tooltip
                content={<RevenueTip currency={currency} />}
                cursor={{ fill: "rgba(42,120,214,0.06)" }}
              />
              <Bar dataKey="revenue" fill={BAR} radius={[4, 4, 0, 0]} maxBarSize={44}>
                <LabelList
                  dataKey="revenue"
                  position="top"
                  content={({ x, y, width, value, index }) =>
                    rows[index]?.isLatest && value > 0 ? (
                      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={INK.primary}>
                        {compactAmount(value, country)}
                      </text>
                    ) : null
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="crm-card-foot muted">
        A deal counts in the month it was last updated as Won.
      </div>

      <div className="crm-sr-only">
        <table>
        <caption>Won revenue by month</caption>
        <thead>
          <tr><th>Month</th><th>Deals won</th><th>Revenue ({currency})</th></tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.month}><td>{d.fullLabel}</td><td>{d.won}</td><td>{d.revenue}</td></tr>
          ))}
        </tbody>
      </table>
      </div>
    </Card>
  );
};

export default RevenueTrend;
